import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Mail, Check, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ca17e8cf-048e-4a40-9d83-d545b8623e13/artifacts/uzgvzj2q_logo-1-768x339.png";

function NewsletterModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !email.includes("@")) { setError("Introduce un correo válido."); return; }
    if (!consent) { setError("Debes aceptar recibir información por correo electrónico."); return; }
    setStatus("loading");
    try {
      await api.post("/newsletter/subscribe", { email: email.trim(), consent });
      try { localStorage.setItem("estrofa_nl_subscribed", "1"); } catch (e) {}
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setError(err?.response?.data?.detail || "No se pudo completar la suscripción.");
    }
  };

  return createPortal(
    <div className="anim-fade-in fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-5" onClick={onClose} data-testid="newsletter-modal">
      <div className="anim-pop-in relative w-full max-w-md rounded-3xl overflow-hidden bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 h-9 w-9 rounded-full bg-black/5 flex items-center justify-center hover:bg-black/10" data-testid="newsletter-close"><X size={18} /></button>
        <div className="h-2 w-full bg-gradient-to-r from-[#2A00D9] via-[#B4D844] to-[#2A00D9]" />
        <div className="p-8">
          {status === "done" ? (
            <div className="text-center py-6" data-testid="newsletter-success">
              <div className="h-14 w-14 rounded-full bg-[#B4D844] text-black flex items-center justify-center mx-auto mb-5"><Check size={28} /></div>
              <h3 className="display text-3xl mb-2">¡Bienvenido/a!</h3>
              <p className="text-[#4A4A4A]">Ya formas parte de la comunidad de La Estrofa. Pronto tendrás noticias en tu correo.</p>
              <button onClick={onClose} className="btn-cobalt mt-7 mx-auto" data-testid="newsletter-done-btn">Cerrar</button>
            </div>
          ) : (
            <>
              <p className="eyebrow mb-3 inline-flex items-center gap-2"><Sparkles size={14} /> Newsletter</p>
              <h3 className="display text-3xl mb-2">Únete a nuestra comunidad</h3>
              <p className="text-[#4A4A4A] mb-6">Recibe lo mejor de la música: lanzamientos, entrevistas y la agenda de Madrid, directo a tu correo.</p>
              <form onSubmit={submit} className="space-y-4">
                <div className="relative">
                  <Mail size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@correo.com"
                    className="w-full rounded-full border border-black/15 pl-11 pr-4 py-3.5 focus:outline-none focus:border-[#2A00D9]"
                    data-testid="newsletter-email-input"
                  />
                </div>
                <label className="flex items-start gap-3 cursor-pointer text-sm text-[#4A4A4A]" data-testid="newsletter-consent-label">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-5 w-5 accent-[#2A00D9] shrink-0" data-testid="newsletter-consent-checkbox" />
                  <span>Acepto recibir información y comunicaciones de La Estrofa por correo electrónico.</span>
                </label>
                {error && <p className="text-sm text-[#C0392B]" data-testid="newsletter-error">{error}</p>}
                <button type="submit" disabled={status === "loading"} className="btn-cobalt w-full justify-center !py-3.5 disabled:opacity-60" data-testid="newsletter-submit">
                  {status === "loading" ? "Suscribiendo..." : "Suscribirme"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export function CommunityBar() {
  const [open, setOpen] = useState(false);

  // Apertura automática del modal tras cierto tiempo en la web (una vez por sesión).
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (localStorage.getItem("estrofa_nl_subscribed") === "1") return;
      if (sessionStorage.getItem("estrofa_nl_autoshown") === "1") return;
    } catch (e) {}
    const t = setTimeout(() => {
      setOpen(true);
      try { sessionStorage.setItem("estrofa_nl_autoshown", "1"); } catch (e) {}
    }, 25000);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="community-bar-gradient fixed bottom-0 inset-x-0 z-[45] flex items-center justify-center gap-2 md:gap-3 py-3 px-3 md:px-5 group"
        data-testid="community-bar"
      >
        <img src={LOGO_URL} alt="La Estrofa" width={768} height={339} decoding="async" className="h-5 md:h-6 w-auto shrink-0 transition-transform group-hover:scale-105" />
        <span className="text-[11px] sm:text-sm md:text-base font-bold uppercase tracking-wider md:tracking-[0.2em] text-white text-center leading-tight">Forma parte de la comunidad de La Estrofa</span>
      </button>
      {open && <NewsletterModal onClose={() => setOpen(false)} />}
    </>
  );
}
